import React, { useEffect } from 'react'
import { slideToggle } from '../../utils/globalUtils';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { displayPrice } from '../currencyPrice';
import { TRANSLATION_TYPE } from '../../utils/constants';
import { getTranslation } from '../../reducers/translation';
import { getInterpolatedString } from '../../utils/text';
import { vvPlanPage } from '../../settings.js/dev';
import { formatDateLocalized } from '../../utils/date';

function OrderSummary() {
    const { estimate, displayPurchaseComplete } = useSelector((state) => state.cart);
    const { allowSwitchPlans, switchPlansUrl } = useSelector((state) => state.sessionDetails);
    const { creditsTotal, taxAmount, discountAmount, subTotal, total, period, periodUnit, currencyCode, itemLines, meta, displayTax, subStatus, nextTotal, trialPeriod, trialPeriodUnit, thankyouPageType, termsToCharge, hasConvertingTrial, ongoingTotal, convertingTrialItemName, convertingTrialUpgradePlanName } = estimate;
    const { locale } = useSelector((state) => state.translation);
    const t = useSelector(getTranslation)
    const t_label = t[TRANSLATION_TYPE.LABEL] ?? {}
    const t_message = t[TRANSLATION_TYPE.MESSAGE] ?? {}

    useEffect(() => {
        document.querySelector('#order-summary__more').addEventListener('click', function () {
            this.classList.toggle('active');
            slideToggle(this.previousElementSibling);
        });
    }, [])

    const getPeriodText = () => {
        if (periodUnit && period) {
            if (period === 1) {
                if (periodUnit === "Month")
                    return t_label?.['checkout.label.monthly'] ?? "monthly"
                else if (periodUnit === "Year")
                    return t_label?.['checkout.label.annually'] ?? "annually"
            } else {
                return `every ${period} ${periodUnit.toLowerCase()}`;
            }
        }
        return null;
    }

    // The "/period" suffix on the ongoing-total line needs a NOUN ("/month"), not the adverb
    // getPeriodText() returns ("monthly"). That adverb is correct in "Billed monthly after trial"
    // and wrong here — "$59.90/monthly" is not how a price reads.
    //
    // Only a period of 1 in Month or Year resolves. Anything else returns null and the suffix is
    // omitted entirely rather than emitting broken grammar like "/every 3 year": the line then reads
    // "Then $X going forward", which is the same shape used when no period resolves at all. A
    // billing_period other than 1 does not exist in the catalogue today in any case.
    const billingPeriodNoun = () => {
        if (period !== 1) return null;
        if (periodUnit === "Month") return t_label?.['checkout.label.month'] ?? "month";
        if (periodUnit === "Year") return t_label?.['checkout.label.year'] ?? "year";
        return null;
    }

    // Guarded rather than used directly: a catalog entry could be an empty string, which `??` does
    // not substitute for, and a bare "/" with nothing after it is worse than no suffix.
    const periodNoun = billingPeriodNoun();
    const hasPeriodNoun = typeof periodNoun === 'string' && periodNoun.trim() !== '';

    const getPeriodText2 = (periodUnit) => {
        if (periodUnit) {
            if (periodUnit === "Month")
                return t_label?.['checkout.label.month'] ?? "month"
            else if (periodUnit === "Day")
                return t_label?.['checkout.label.day'] ?? "day"
        }
        return null;
    }

    const displayTitle = () => {
        let plan = itemLines != null ? itemLines.find(line => line.type === "Plan") : null;
        if (plan == null && !convertingTrialItemName)
            return <></>
        const titleText = convertingTrialItemName ?? plan.description;
        // Box, not Typography. This app configures no ThemeProvider, so Typography renders against
        // MUI's default theme and emits a single-class rule that ties with .order-summary__title on
        // specificity — emotion injects at runtime, so it lands later and wins. Default Typography
        // would replace all five of that class's declarations (25px/800/line-height 1 becomes
        // 16px/400/1.5 Roboto) and `variant="inherit"` still emits `margin:0`, which drops the
        // 10px/20px spacing. Re-adding those through `sx` would duplicate main.css in JS. Box given
        // no system props emits an empty rule, so the stylesheet stays authoritative.
        return <>
            <Box className="order-summary__title">{titleText}
                {(switchPlansUrl || allowSwitchPlans) &&
                    <>
                        <a href={switchPlansUrl ? switchPlansUrl : vvPlanPage} class="switch-plan">Switch Plan</a>
                    </>
                }
            </Box>
        </>
    }

    const displayItemTrial = (item) => {
        if (!item?.isTrial)
            return false;
        if (subStatus === "InTrial" && subTotal > 0)
            return false;
        return true;
    }

    // Set only when the backend resolved a multi-year offer, on both the renewal and the new-customer
    // path. Guarded rather than compared directly because it is absent on every ordinary checkout.
    const isMultiTerm = Number(termsToCharge) > 1;

    // A multi-year purchase comes back as one plan line per term, and Chargebee gives every line
    // the same description — so without the service period they render as an identical row repeated
    // N times, which reads as a duplicate charge. Append the period each line actually covers.
    const planCostLabel = (item) => {
        const base = periodUnit === "Month"
            ? t_label?.['checkout.label.monthly_cost'] ?? "Monthly Cost"
            : t_label?.['checkout.label.annual_cost'] ?? "Annual Cost";
        if (isMultiTerm && item?.periodFrom && item?.periodTo) {
            const from = formatDateLocalized(item.periodFrom, locale);
            const to = formatDateLocalized(item.periodTo, locale);
            // Fall back to the bare label if either date will not format — better an unlabelled row
            // than "Annual Cost (undefined - undefined)".
            if (from && to) {
                return `${base} (${from} - ${to})`;
            }
        }
        return base;
    }

    const displayItemByType = (itemType, itemTitle) => {
        let items = itemLines != null ? itemLines.filter(line => line.type === itemType) : [];
        if (items == null || items.length === 0)
            return <></>
        return <>
            {
                itemTitle &&
                <div className="order-summary__addon">
                    {itemTitle}
                </div>
            }

            {
                items.map((item) => {
                    return (
                        <>
                            {
                                !(itemType === "Plan" && item.isTrial && subTotal > 0) &&
                                < div className="order-summary__item">
                                    {itemType === "Plan" ? planCostLabel(item) : item.description}

                                    {
                                        item.isTrial ?
                                            <span className='order-summary__free-trial'>{displayItemTrial(item) ? t_label?.['billing.label.free_trial'] : ""}</span> :
                                            <span>{displayPrice(item.amount, currencyCode)}</span>
                                    }
                                </div>
                            }
                        </>
                    )
                })
            }
        </>
    }

    const displayTaxAmount = () => {
        if (displayTax) {
            return displayPrice(taxAmount, currencyCode);
        }
        return t_label?.['checkout.label.calculated_at_payment'] ?? "Calculated at Payment";
    }

    const displayPlanMeta = (meta) => {
        const header = meta?.header;
        const features = meta?.data;
        const checkStyleClass = "text-green";

        return <>
            {
                header &&
                <div class="order-summary__heading" dangerouslySetInnerHTML={{ __html: header }} />
            }

            {
                features && features.map((data) => {
                    return <>
                        {/* <div class="mt-2 flex items-start gap-1.5">
                            <svg class={`h-4 w-4 shrink-0 ${checkStyleClass}`}><use href="/img/sprite.svg#check"></use></svg>
                            <div class="" dangerouslySetInnerHTML={{ __html: data.header }} />
                        </div> */}

                        <div class="order-summary__list--alt">
                            <div class="" dangerouslySetInnerHTML={{ __html: data.header }} />
                        </div>

                        <div class="order-summary__list">
                            {data.description}
                        </div>
                    </>
                })
            }
        </>
    }

    return (
        <>
            <div className="order-summary">
                <div>
                    <div className="order-summary__overline">
                        <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
                            <rect width="32" height="32" fill="#21205F" />
                            <path d="M16 16H0L16 32V16Z" fill="#F2E5AF" />
                            <rect x="-11.7573" y="-20" width="100" height="6" transform="rotate(45 -11.7573 -20)" fill="white" />
                            <rect x="0.5" y="0.5" width="31" height="31" stroke="#6F7B8E" />
                        </svg>
                        VectorVest
                    </div>

                    {displayTitle()}

                    {
                        !displayPurchaseComplete && <>
                            {displayItemByType("Plan", null)}
                            {displayItemByType("Addon", t_label?.['checkout.label.addons'] ?? "ADD-ONS")}
                            {displayItemByType("Charge", t_label?.['checkout.label.charges'] ?? "CHARGES")}

                            {/* The Discount row ALWAYS renders before Subtotal (product rule,
                                2026-08-31). This used to be two branches choosing the order by
                                comparing total against subTotal + tax - credits — which also
                                silently suppressed BOTH rows whenever any operand failed
                                parseFloat (creditsTotal arrives null on several estimate shapes,
                                and parseFloat(null) is NaN). One unconditional order, gated only
                                on a discount existing, replaces the arithmetic entirely. */}
                            {/* discountAmount != null, not just > 0: in-trial estimates arrive with a
                                null discount AND a null subTotal, and the old branches (all of which
                                required a non-null discount) rendered neither row there. Keep that. */}
                            {
                                discountAmount != null && <>
                                    {
                                        discountAmount > 0 &&
                                        <div className="order-summary__subtotal order-summary__border-top">{t_label?.['checkout.label.discount'] ?? 'Discount'}<span>{displayPrice(discountAmount, currencyCode)}</span></div>
                                    }
                                    <div className={discountAmount > 0 ? "order-summary__subtotal" : "order-summary__subtotal order-summary__border-top"}>{t_label?.['checkout.label.subtotal'] ?? 'Subtotal'}<span>{displayPrice(subTotal, currencyCode)}</span></div>
                                </>
                            }

                            <div className="order-summary__subtotal">{t_label?.['checkout.label.tax'] ?? 'Tax'}<span>{displayTaxAmount()}</span></div>

                            {
                                creditsTotal > 0 &&
                                <div className="order-summary__subtotal">{t_label?.['checkout.label.credits'] ?? 'Credits'}<span>{displayPrice(creditsTotal, currencyCode)}</span></div>
                            }

                            <div className="order-summary__total">{t_label?.['checkout.label.total'] ?? 'Total'}<span>{displayPrice(total, currencyCode)}</span></div>

                            {
                                /* Hidden on a multi-year purchase. periodUnit is the plan's own billing
                                   period, so this line reads "Subscription billed annually" — true of the
                                   plan, but wrong about this order: the customer is paying 2 or 3 years
                                   up front and will not be billed again next year. The per-term rows
                                   above already carry the dates each term covers, so the coverage is
                                   still on screen. */
                                (subStatus !== "InTrial") && periodUnit && !isMultiTerm && thankyouPageType != 'COURSE'  && thankyouPageType != 'ADD_ON' && <>
                                    {/* <div className="order-summary__annualy">Subscription billed {getPeriodText()}</div> */}
                                    <div className="order-summary__annualy">
                                        {getInterpolatedString(t_message?.['checkout.message.subscription_billed_period'], {
                                            'getPeriodText()': getPeriodText().toLowerCase(),
                                        })}
                                    </div>
                                </>
                            }

                            {/* Renewal disclosure for an ordinary addon bought by an existing subscriber.
                                The total above is prorated to term end, so it matches neither the link the
                                customer clicked nor the renewal — and after frequency normalization a
                                $69/month link can be quoted as a $695/year addon. Keyed on the backend
                                having supplied ongoingTotal, NOT on thankyouPageType: the update path never
                                stamps that field for this flow, and stamping it would change thank-you
                                routing. Reads "{addon} renews at $X/period" rather than the trial's
                                "Then $X going forward": the amount is the addon's own price, and naming
                                the addon is what stops it being read as the whole subscription's. */}
                            {
                                (subStatus !== "InTrial") && !hasConvertingTrial && !isMultiTerm && ongoingTotal != null &&
                                <Box className="order-summary__trial" sx={{ display: 'block', marginTop: '4px', paddingTop: 0, borderTop: 'none' }}>
                                    {getInterpolatedString(
                                        t_message?.['checkout.message.addon_renews_at'] ?? '${addonName} renews at ',
                                        { 'addonName': itemLines?.find(line => line.type === "Addon")?.description ?? (t_label?.['checkout.label.addon'] ?? 'Add-on') }
                                    )}
                                    <span>{displayPrice(ongoingTotal, currencyCode)}</span>
                                    {hasPeriodNoun ? `/${periodNoun}` : ''}
                                </Box>
                            }

                            {
                                (subStatus === "InTrial") &&
                                <>
                                    {/* <div class="order-summary__trial">{`Billed ${getPeriodText()} after trial`}<span>{displayPrice(nextTotal, currencyCode)} </span></div> */}
                                    {/* <div class="order-summary__note">Cancel anytime before your {trialPeriod} {trialPeriodUnit.toLowerCase()} trial ends</div> */}

                                    <div class="order-summary__trial">
                                    {
                                            thankyouPageType != 'ADD_ON' ? (
                                                thankyouPageType === 'UPGRADE' ? (
                                                    t_message?.['checkout.message.prorated_billing_after_trial']
                                                ) : (
                                                    getInterpolatedString(t_message?.['checkout.message.billing_after_trial'], {
                                                        'getPeriodText()': getPeriodText().toLowerCase()
                                                    })
                                                )
                                            ) : (
                                                'Billed one-time after trial'
                                            )
                                    }
                                    <span>{displayPrice(nextTotal, currencyCode)} </span>
                                    </div>

                                    {
                                        hasConvertingTrial && ongoingTotal != null &&
                                        <div class="order-summary__trial" style={{ display: 'block', marginTop: '4px', paddingTop: 0, borderTop: 'none' }}>
                                            {t_message?.['checkout.message.converting_trial_ongoing_prefix'] ?? 'Then '}
                                            <span>{displayPrice(ongoingTotal, currencyCode)}</span>
                                            {/* Omit "/period" entirely when the period is unknown, rather than
                                                emitting a dangling "/". The space is kept either way so the
                                                suffix does not butt against the amount. */}
                                            {hasPeriodNoun ? `/${periodNoun} ` : ' '}
                                            {t_message?.['checkout.message.converting_trial_ongoing_suffix'] ?? 'going forward'}
                                        </div>
                                    }

                                    {/* Plan-gated converting trial only. The title above shows the
                                        converting addon's name, and that title is the only place the
                                        plan line's description is rendered — so without this line the
                                        plan change at conversion is invisible to the customer. Sits
                                        next to the ongoing-total line because it explains what that
                                        amount is for. */}
                                    {
                                        hasConvertingTrial && convertingTrialUpgradePlanName &&
                                        <div class="order-summary__note">
                                            {getInterpolatedString(
                                                t_message?.['checkout.message.converting_trial_plan_change']
                                                ?? 'Your plan changes to ${planName} when the trial converts',
                                                { 'planName': convertingTrialUpgradePlanName }
                                            )}
                                        </div>
                                    }

                                    {
                                        thankyouPageType === 'UPGRADE' &&
                                        <div class="order-summary__note">
                                            {t_message?.['checkout.message.prorated_tax_note'] ?? 'Applicable taxes will be added at conversion and are not reflected in the amount above.'}
                                        </div>
                                    }

                                    <div class="order-summary__note">
                                        {getInterpolatedString(t_message?.['checkout.message.cancel_trial'], {
                                            'trialPeriod': trialPeriod,
                                            'trialPeriodUnit': getPeriodText2(trialPeriodUnit)
                                        })}
                                    </div>
                                </>
                            }
                        </>
                    }

                    {
                        displayPurchaseComplete && <>
                            <div className='order-summary__addon'></div>
                            <div className="order-purchase-complete">{t_label?.['checkout.label.purchase_complete'] ?? 'Purchase Complete'}</div>
                            <div>{t_message?.['checkout.message.finish_account_creation'] ?? 'Finish account creation to access VectorVest'}</div>
                            <div className='order-summary__addon'></div>
                        </>
                    }

                </div>
                <div>
                    <div id="order-summary__spoiler" className="order-summary__spoiler">
                        {
                            displayPlanMeta(meta)
                        }
                    </div>
                    <button type="button" data-more={t_label?.['checkout.label.see_whats_included']} data-less={t_label?.['checkout.label.see_less']} id="order-summary__more" className="order-summary__more"></button>
                </div>
            </div>
        </>
    )
}

export default OrderSummary